import { apiSlice } from './api-slice';
import {
  Response,
  PaginatedPhase,
  TeamMember,
  Phase,
  Sortable,
} from '../models';
import { PAGINATION_SIZE } from '../constants';

export const phasesApiSlice = apiSlice.injectEndpoints({
  endpoints: builder => ({
    getAllPhases: builder.query<
      Response<
        Sortable<
          Pick<
            Phase,
            'id' | 'phaseName' | 'phaseCode' | 'progress' | 'phaseStartDate'
          > & {
            members: number;
            phaseEndDate: Date;
          }
        >
      >,
      PaginatedPhase
    >({
      query: ({ projectId, ...params }) => ({
        url: `company/phase/allPhases`,
        method: 'GET',
        params: { size: PAGINATION_SIZE, ...params, projectId },
      }),
      providesTags: result =>
        result?.data.content.length
          ? result.data.content.map(({ id }) => ({ type: 'Phase', id }))
          : ['Phase'],
    }),
    getPhaseById: builder.query<
      Response<Phase>,
      { projectId: number; phaseId: number }
    >({
      query: ({ projectId, phaseId }) => ({
        url: `company/project/${projectId}/phase/${phaseId}`,
        method: 'GET',
      }),
      providesTags: (_, __, { phaseId }) => [{ type: 'Phase', id: phaseId }],
    }),
    createPhase: builder.mutation<
      Response<Phase>,
      {
        projectId: number;
        phases: Pick<
          Phase,
          'phaseName' | 'phaseCode' | 'phaseEstimatedEndDate' | 'phaseStartDate'
        >[];
      }
    >({
      query: ({ projectId, phases }) => ({
        url: `company/project/${projectId}/phase`,
        method: 'POST',
        body: phases,
      }),
      invalidatesTags: [{ type: 'Phase' }],
    }),
    updatePhase: builder.mutation<
      Response<Phase>,
      { projectId: number; phase: Phase }
    >({
      query: ({ projectId, phase }) => ({
        url: `company/project/${projectId}/phase/${phase.id}`,
        method: 'PUT',
        body: phase,
      }),
      invalidatesTags: result => [{ type: 'Phase', id: result?.data.id }],
    }),
    deletePhase: builder.mutation<
      Response<null>,
      { projectId: number; phaseId: number }
    >({
      query: ({ projectId, phaseId }) => ({
        url: `company/project/${projectId}/phase/${phaseId}`,
        method: 'DELETE',
      }),
      invalidatesTags: () => {
        return [{ type: 'Phase' }];
      },
    }),
    getTeamMemberByPhase: builder.query<
      Response<TeamMember[]>,
      { projectId: number; phaseId: number }
    >({
      query: ({ projectId, phaseId }) => ({
        url: `company/project/${projectId}/phase/${phaseId}/teamMember`,
        method: 'GET',
      }),
    }),
  }),
});

export const {
  useGetPhaseByIdQuery,
  useGetAllPhasesQuery,
  useCreatePhaseMutation,
  useUpdatePhaseMutation,
  useDeletePhaseMutation,
  useGetTeamMemberByPhaseQuery,
} = phasesApiSlice;
